import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatCZK } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Send, CalendarPlus, ShieldCheck, ExternalLink, Wallet, Clock, Copy } from "lucide-react";
import { toast } from "sonner";
import { PaymentDialog, PostponeDialog } from "@/components/superadmin/Subscription";
import { PAID_PLANS, currentInvoice, isStripe, periodAmount, recommendPlan, daysTo } from "@/components/superadmin/firm";

// Akce nad firmou (209) — stejné z karty na tabuli i z hlavičky detailu. Jedna akce = jeden
// klik; tam, kde je potřeba něco doplnit (tarif, datum platby), se otevře malé okno.

const A = {
  paylink: { kind: "paylink", label: "Poslat platební odkaz", icon: Send },
  extend: { kind: "extend", label: "Prodloužit demo +14", icon: CalendarPlus },
  restore: { kind: "restore", label: "Obnovit firmu", icon: ShieldCheck },
  pay: { kind: "pay", label: "Zaevidovat platbu", icon: Wallet },
  postpone: { kind: "postpone", label: "Odložit zámek", icon: Clock },
  sendlink: { kind: "sendlink", label: "Poslat odkaz na platbu", icon: Send },
  fakturoid: { kind: "link", label: "Otevřít ve Fakturoidu", icon: ExternalLink },
};

// Odklad zámku má smysl jen u předplatného fakturou ve fázi „nezaplaceno / zamčeno“ (ne u ručního „Čeká na úhradu“).
const canPostpone = (r) => r.plan_status === "active" && ["unpaid", "locked"].includes(r.billing?.phase);

/** Hlavní akce podle stavu firmy (max. 2, první je primární). */
export function actionsFor(r) {
  if (!r || r.is_test) return [];
  const k = r.display_status?.key, b = r.billing || {};
  switch (k) {
    case "demo_bezi": return [A.paylink, A.extend];
    case "demo_vyprselo": return [A.extend, A.paylink];
    case "pozastaveno": return [A.restore];
    case "nezaplaceno": return canPostpone(r) ? [A.pay, A.postpone] : [A.pay];
    case "ceka_uhrada": return isStripe(r) ? [A.sendlink] : canPostpone(r) ? [A.pay, A.postpone] : [A.pay];
    case "ukonceno": return (r.plan || "demo") === "demo" ? [A.extend] : [];
    default: {
      if (isStripe(r)) return [];
      const ni = currentInvoice(b);
      if (b.phase === "invoice_due" && ni?.url) return [{ ...A.fakturoid, url: ni.url }, A.pay];
      return [A.pay];
    }
  }
}

/** Jediné tlačítko na kartě tabule — jen když je opravdu co dělat. */
export function cardAction(r, col) {
  const k = r.display_status?.key;
  if (r.is_test || k === "ukonceno" || k === "plati" && col === "plati") return null;
  if (k === "demo_bezi") {
    const n = daysTo(r.trial_ends_at);
    return n != null && n <= 3 && r.active_projects > 0 ? A.paylink : null;
  }
  return actionsFor(r)[0] || null;
}

export function useFirmActions({ kpi, reload }) {
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState(null);
  const [postpone, setPostpone] = useState(null);
  const [link, setLink] = useState(null);
  const [sent, setSent] = useState(null);     // {r, url, emailed} — odkaz na platbu k ručnímu poslání

  const call = async (path, body, ok) => {
    setBusy(true);
    try {
      const { data } = await api.post(path, body || {});
      if (ok) toast.success(ok);
      await reload?.();
      return data || {};
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return null; }
    finally { setBusy(false); }
  };

  const run = (action, r) => {
    const id = r.id;
    switch (action.kind) {
      case "link": window.open(action.url, "_blank", "noopener"); break;
      case "pay": setPay(r); break;
      case "postpone": setPostpone(r); break;
      case "paylink": setLink(r); break;
      case "extend": call(`/admin/companies/${id}/extend-trial`, {}, `Demo prodlouženo o 14 dní · ${r.name}`); break;
      case "restore": call(`/admin/companies/${id}/reactivate`, {}, `Firma obnovena · ${r.name}`); break;
      case "sendlink":
        call(`/admin/companies/${id}/billing/send-link`, { origin_url: window.location.origin }).then(async (d) => {
          if (!d) return;
          try { if (d.url) await navigator.clipboard.writeText(d.url); } catch { /* schránka nemusí být dostupná */ }
          toast.success(d.emailed ? `Odkaz na platbu odeslán majiteli · ${r.name}` : "Odkaz vytvořen, e-mail se nepodařilo odeslat — pošli ho ručně.");
          if (d.url) setSent({ r, url: d.url, emailed: !!d.emailed });
        });
        break;
      default: break;
    }
  };

  const dialogs = (
    <>
      {pay && (
        <PaymentDialog open onOpenChange={(o) => { if (!o) setPay(null); }} c={pay} b={pay.billing || {}} amount={periodAmount(pay)} busy={busy}
          onSubmit={async (body) => { if (await call(`/admin/companies/${pay.id}/billing/payment`, body, `Platba zaevidována · ${pay.name}`)) setPay(null); }} />
      )}
      {postpone && (
        <PostponeDialog open onOpenChange={(o) => { if (!o) setPostpone(null); }} b={postpone.billing || {}} busy={busy}
          onSubmit={async (until) => { if (await call(`/admin/companies/${postpone.id}/billing/postpone`, { until }, until ? `Zámek odložen · ${postpone.name}` : "Odklad zrušen")) setPostpone(null); }} />
      )}
      {link && <PayLinkDialog r={link} kpi={kpi} busy={busy} onClose={() => setLink(null)} call={call} />}
      {sent && (
        <Dialog open onOpenChange={(o) => { if (!o) setSent(null); }}>
          <DialogContent className="max-w-md" data-testid="sa-sendlink-dialog">
            <DialogHeader>
              <DialogTitle>Odkaz na platbu</DialogTitle>
              <DialogDescription>{sent.r.name} — {sent.emailed ? "odeslán majiteli e-mailem. Pro jistotu ho máš i tady." : "e-mail se nepodařilo odeslat, pošli odkaz ručně."}</DialogDescription>
            </DialogHeader>
            <CopyField url={sent.url} testid="sa-sendlink-url" />
            <div className="flex justify-end"><Button variant="outline" onClick={() => setSent(null)} className="w-full sm:w-auto">Hotovo</Button></div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
  return { run, dialogs, busy };
}

function CopyField({ url, testid }) {
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Odkaz zkopírován"); } catch { toast.error("Kopírování se nepovedlo"); } };
  return (
    <div className="flex gap-2" data-testid={testid}>
      <Input readOnly value={url} onFocus={(e) => e.target.select()} className="h-11 text-xs w-full min-w-0 font-mono" aria-label="Odkaz" />
      <Button type="button" variant="outline" className="h-11 w-11 shrink-0" onClick={copy} aria-label="Zkopírovat odkaz"><Copy size={15} /></Button>
    </div>
  );
}

function PayLinkDialog({ r, kpi, busy, onClose, call }) {
  const [plan, setPlan] = useState(() => (r.plan && r.plan !== "demo" ? r.plan : recommendPlan(r)));
  const [url, setUrl] = useState(null);
  const send = async () => {
    const d = await call(`/admin/companies/${r.id}/billing/checkout`, { plan, origin_url: window.location.origin });
    if (!d?.checkout_url) return;
    setUrl(d.checkout_url);
    let copied = false;
    try { await navigator.clipboard.writeText(d.checkout_url); copied = true; } catch { /* schránka nemusí být dostupná */ }
    toast.success(d.emailed ? `Odkaz odeslán majiteli${copied ? " a zkopírován" : ""} · ${r.name}` : "Odkaz vytvořen, e-mail se nepodařilo odeslat — pošli ho ručně.");
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="sa-paylink-dialog">
        <DialogHeader>
          <DialogTitle>Platební odkaz</DialogTitle>
          <DialogDescription>{r.name} — majitel dostane e-mail s odkazem na platbu kartou.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-1 p-1 rounded-[14px] bg-secondary" role="radiogroup" aria-label="Tarif">
            {PAID_PLANS.map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={plan === k} onClick={() => setPlan(k)} data-testid={`sa-paylink-plan-${k}`}
                className={`min-h-[52px] rounded-[11px] px-2 text-sm font-bold transition-colors ${plan === k ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {l}
                <span className="block text-[11px] font-semibold text-muted-foreground tabular-nums">{formatCZK(kpi?.plans?.[k]?.price || 0)}</span>
              </button>
            ))}
          </div>
          {plan === recommendPlan(r) && (r.plan || "demo") === "demo" && (
            <p className="text-xs text-muted-foreground">Navrženo podle využití dema ({r.user_count || 0} lidí, {r.active_projects || 0} zakázek).</p>
          )}
          {url && <CopyField url={url} testid="sa-paylink-url" />}
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-1">
            <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">{url ? "Hotovo" : "Zrušit"}</Button>
            <Button disabled={busy} onClick={send} className="w-full sm:w-auto font-bold" data-testid="sa-paylink-send">
              <Send size={15} /> {url ? "Poslat znovu" : "Poslat odkaz"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
